'use client';

import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  displayValue?: (value: number | undefined) => string | number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  forceLightMode?: boolean; // Force light mode styling (for public pages)
}

export default function Input({
  label,
  error,
  helperText,
  id,
  required,
  disabled,
  className = '',
  displayValue,
  value,
  onChange,
  onFocus,
  type = 'text',
  forceLightMode = false,
  ...props
}: InputProps) {
  // Use dark mode context if available, otherwise default to light mode
  // IMPORTANT: Always call hooks unconditionally (React rules of hooks)
  let contextDarkMode = false;
  try {
    const context = useDarkMode();
    contextDarkMode = context.darkMode;
  } catch {
    // Context not available (e.g., public pages), use light mode
    contextDarkMode = false;
  }

  // Apply forceLightMode override if needed
  const darkMode = forceLightMode ? false : contextDarkMode;

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const inputId = id || generatedId;

  // Handle value display for number inputs
  const processedValue = React.useMemo(() => {
    if (type === 'number') {
      // If custom displayValue function is provided, use it
      if (displayValue && value !== undefined) {
        return displayValue(Number(value));
      }
      // Default behavior: show empty string for 0 values
      const numValue = Number(value);
      if (numValue === 0 || value === undefined || value === null || value === '') {
        return '';
      }
      return value;
    }
    return value;
  }, [type, displayValue, value]);

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className={`mb-2 block text-[13px] font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <input
        id={inputId}
        type={type}
        value={processedValue}
        onChange={onChange}
        onFocus={onFocus}
        required={required}
        disabled={disabled}
        className={`block w-full [appearance:textfield] rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all [-webkit-text-fill-color:inherit] sm:text-sm/6 [&:-webkit-autofill]:[-webkit-text-fill-color:inherit] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
          darkMode
            ? 'bg-white/5 text-gray-100 outline-white/10 placeholder:text-gray-500'
            : 'bg-white text-gray-900 outline-gray-300 placeholder:text-gray-400'
        } ${
          darkMode
            ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
            : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
        } ${
          error
            ? darkMode
              ? 'outline-red-500/50 focus:outline-red-500'
              : 'outline-red-500 focus:outline-red-500'
            : ''
        } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''} `}
        {...props}
      />

      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
